import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Check, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Refresh user data to get updated subscription
        await checkAuth();
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setTimeout(() => pollStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Status check error:", error);
      setTimeout(() => pollStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-8 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#002FA7] animate-spin" />
            </div>
            <h1 className="text-xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100 mb-2">
              Processing payment...
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100 mb-2">
              Subscription activated!
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
              Thank you for subscribing to roster.ai Pro. You now have full access to all features.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-red-600 text-2xl font-bold">!</span>
            </div>
            <h1 className="text-xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100 mb-2">
              Payment issue
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
              We couldn't confirm your payment. Please try again or contact support.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/subscription")}
                data-testid="retry-btn"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="back-dashboard-btn"
              >
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
